import { useState, useId } from 'react'
import { useDropdown } from '../utils/DropdownContext'

type Option = { 
  title: string
  value: string 
}

interface DropDownProps {
  placeholder?: string
  options: Option[]
  value?: string
  onChange?: (value: string) => void
  onSelect?: (value: string) => void
  className?: string
  disabled?: boolean
  isActive?: boolean
  size?: 'small' | 'large'
}

const DropDown = ({ 
  placeholder = 'Select option',
  options,
  value,
  onChange,
  onSelect,
  className = '',
  disabled = false,
  isActive = false,
  size = 'large'
}: DropDownProps) => {
  const dropdownId = useId()
  const { openDropdown, setOpenDropdown } = useDropdown()
  const showDrop = openDropdown === dropdownId
  const [internalValue, setInternalValue] = useState<string>(options[0]?.value || '')
  
  const currentValue = value !== undefined ? value : internalValue
  const selectedOption = options.find(option => option.value === currentValue)
  const displayText = selectedOption?.title || placeholder
  const hasSelection = currentValue && currentValue !== options[0]?.value

  const handleSelect = (option: Option) => {
    if (value === undefined) {
      setInternalValue(option.value)
    }
    onChange?.(option.value)
    onSelect?.(option.value)
    setOpenDropdown(null)
  }

  const toggleDropdown = () => {
    if (!disabled) {
      setOpenDropdown(showDrop ? null : dropdownId)
    }
  }

  return (
    <div className={` relative ${className}`}>
      <div 
              className={`${size === 'small' ? 'h-[30px] min-h-[30px] text-[14px] font-[400] border-[1px] border-[#D9D9D9] min-w-[120px]' : 'h-[46px] min-h-[46px] font-[600] text-[16px] min-w-[150px]'} rounded-[10px] flex items-center justify-between p-2 cursor-pointer ${
                  (isActive || hasSelection) ? '' : 'bg-muted'
        } ${
          disabled ? 'opacity-50 cursor-not-allowed' : ((isActive || hasSelection) ? 'hover:opacity-90' : 'hover:bg-muted')
        }`}
        style={(isActive || hasSelection) ? {
          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))'
        } : {}}
        onClick={toggleDropdown}
      >
        <span className={` ${
          (isActive || hasSelection) ? 'text-[#FBFBFE]' : 'text-foreground'
        }`}>{displayText}</span>
        <svg 
          width="12" 
          height="7" 
          viewBox="0 0 12 7" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className={`transform transition-transform ${showDrop ? 'rotate-180' : ''}`}
        >
          <path d="M10.1693 1.3335L6.0026 5.50016L1.83594 1.3335" stroke={(isActive || hasSelection) ? "#FBFBFE" : "#3F3E42"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      
      {showDrop && (
        <div className='absolute top-full left-0 right-0 mt-2 bg-card rounded-[10px] p-3 z-10' style={{ boxShadow: "0px 13px 15px 5px #0302084D" }}>
          {options.map((option) => (
            <div 
              key={option.value}
              className='flex justify-between items-center p-2 hover:bg-muted rounded cursor-pointer'
              onClick={() => handleSelect(option)}
            >
                  <span className='text-foreground font-[400] text-[16px]'>{option.title}</span>
              {currentValue === option.value && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DropDown